import React, { useMemo } from 'react';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';

const LINE_COLORS = [
    CHART_COLORS.chart.line1,
    CHART_COLORS.chart.line2,
    CHART_COLORS.chart.line3,
    CHART_COLORS.chart.line4,
    CHART_COLORS.chart.line5,
];

export const ProductSalesTrendChart: React.FC<BaseChartProps> = () => {
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const { chartData, topProducts } = useMemo(() => {
        if (!finance?.dailyBreakdown) return { chartData: [], topProducts: [] };

        const productTotals: Record<string, { name: string; total: number }> = {};
        const dailyProductRevenue: Record<string, Record<string, number>> = {};

        const sortedDates = Object.keys(finance.dailyBreakdown).sort();

        for (const date of sortedDates) {
            const day = finance.dailyBreakdown[date];
            if (!day?.breakdown) continue;

            dailyProductRevenue[date] = {};

            for (const item of day.breakdown) {
                if (item.type === 'REVENUE' && item.category === 'product_sale') {
                    const id = item.entityId as string;
                    const name = (item.entityName as string) || id;
                    const value = Number(item.value) || 0;

                    if (!productTotals[id]) {
                        productTotals[id] = { name, total: 0 };
                    }
                    productTotals[id].total += value;
                    dailyProductRevenue[date][id] = (dailyProductRevenue[date][id] || 0) + value;
                }
            }
        }

        const top5 = Object.entries(productTotals)
            .sort(([, a], [, b]) => b.total - a.total)
            .slice(0, 5)
            .map(([id, info]) => ({ id, name: info.name }));

        const data = sortedDates.map((date) => {
            const point: Record<string, unknown> = {
                date,
                formattedDate: format(parseISO(date), 'MMM dd'),
            };
            for (const product of top5) {
                point[product.id] = dailyProductRevenue[date]?.[product.id] || 0;
            }
            return point;
        });

        return { chartData: data, topProducts: top5 };
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0 || topProducts.length === 0, [chartData, topProducts]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="formattedDate"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number, name: string) => {
                                const product = topProducts.find((p) => p.id === name);
                                return [
                                    formatCompactNumber(value, currencySymbol),
                                    product?.name || name,
                                ];
                            }}
                            labelFormatter={(label: string) => label}
                        />
                        <Legend
                            formatter={(value: string) => {
                                const product = topProducts.find((p) => p.id === value);
                                const name = product?.name || value;
                                return name.length > 20 ? `${name.slice(0, 20)}...` : name;
                            }}
                        />
                        {topProducts.map((product, index) => (
                            <Line
                                key={product.id}
                                type="monotone"
                                dataKey={product.id}
                                stroke={LINE_COLORS[index]}
                                strokeWidth={CHART_STYLES.line.strokeWidth}
                                dot={CHART_STYLES.line.dot}
                                activeDot={{ ...CHART_STYLES.line.activeDot, fill: LINE_COLORS[index] }}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
